import { createSlice } from '@reduxjs/toolkit'
import moment from 'moment'

export const TIMELINE_WORKOUT_CACHE_MAX_DAYS = 28

const initialState = {
    timelineWorkoutCacheKey: '',
    timelineWorkoutCache: {},
}

const programDefinitionSlice = createSlice({
    name: 'programDefinition',
    initialState,
    reducers: {
        initializeTimelineWorkoutCache: (state, action) => {
            const { 
                programDefinitionId, 
                programStartDate, 
                weeklySchedule, 
                defaultExercisesPerWorkout, 
                workoutDateActivationOverrides, 
                workoutDateDeactivationOverrides,
                exercisesPerWorkoutOverrides,
                goalRankingOverrides,
            } = action.payload

            const workoutDateActivationOverridesKey = JSON.stringify([...(workoutDateActivationOverrides ?? [])].sort())
            const workoutDateDeactivationOverridesKey = JSON.stringify([...(workoutDateDeactivationOverrides ?? [])].sort())
            const exercisesPerWorkoutOverridesKey = (() => {
                const o = exercisesPerWorkoutOverrides && typeof exercisesPerWorkoutOverrides === 'object' ? exercisesPerWorkoutOverrides : {}
                const keys = Object.keys(o).sort()
                const normalized = {}
                for (const k of keys) {
                    normalized[k] = o[k]
                }
                return JSON.stringify(normalized)
            })()
            const goalRankingOverridesKey = (() => {
                const o = goalRankingOverrides && typeof goalRankingOverrides === 'object' ? goalRankingOverrides : {}
                const keys = Object.keys(o).sort()
                const normalized = {}
                for (const k of keys) {
                    const ids = o[k]
                    normalized[k] = Array.isArray(ids) ? [...ids] : ids
                }
                return JSON.stringify(normalized)
            })()

            var timelineWorkoutCacheKey = `${programDefinitionId}|${programStartDate}|${JSON.stringify(weeklySchedule)}|${defaultExercisesPerWorkout}|`
            timelineWorkoutCacheKey += `${workoutDateActivationOverridesKey}|${workoutDateDeactivationOverridesKey}|${exercisesPerWorkoutOverridesKey}|${goalRankingOverridesKey}`

            if (state.timelineWorkoutCacheKey !== timelineWorkoutCacheKey) {
                state.timelineWorkoutCacheKey = timelineWorkoutCacheKey
                state.timelineWorkoutCache = {}
            }
        },
        addWorkoutToTimelineWorkoutCache: (state, action) => {
            // Add workout to cache
            
            const { timelineStartDate, isoDate, assumptionTrace, rankedGoals, workout } = action.payload
            
            state.timelineWorkoutCache[isoDate] = {
                assumptionTrace: assumptionTrace,
                rankedGoals: rankedGoals,
                workout: workout
            }

            // Remove farthest cached workout if cache is full

            const evictionCount = Math.max(0, Object.keys(state.timelineWorkoutCache).length - TIMELINE_WORKOUT_CACHE_MAX_DAYS)

            for (let i = 0; i < evictionCount; i += 1) {
                DeleteFurthestCachedWorkoutFromTimeline(state.timelineWorkoutCache, timelineStartDate)
            }
        },
        resetProgramDefinitionState: () => initialState
    },
})

function AbsDayDiffFromTimelineStart(isoDate, timelineStartDate) {
    const day = moment(isoDate, 'YYYY-MM-DD').startOf('day')
    const origin = moment(timelineStartDate).clone().startOf('day')
    return Math.abs(day.diff(origin, 'days'))
}

function DeleteFurthestCachedWorkoutFromTimeline(cache, timelineStartDate) {
    const keys = Object.keys(cache)
    
    if (keys.length === 0) return

    let furthestIso = keys[0]
    let furthestDiff = AbsDayDiffFromTimelineStart(furthestIso, timelineStartDate)

    for (let i = 1; i < keys.length; i += 1) {
        const key = keys[i]

        const diffDays = AbsDayDiffFromTimelineStart(key, timelineStartDate)
        
        if (diffDays > furthestDiff || (diffDays === furthestDiff && key > furthestIso)) {
            furthestDiff = diffDays
            furthestIso = key
        }
    }

    // console.log('Deleting farthest cached workout:', furthestIso)

    delete cache[furthestIso]
}

export const {
    initializeTimelineWorkoutCache,
    addWorkoutToTimelineWorkoutCache,
    resetProgramDefinitionState
} = programDefinitionSlice.actions

export default programDefinitionSlice.reducer
